import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { useCompanyProfile } from '../../hooks/useCompanyProfile';
import { ChevronDown, ChevronUp, CheckCircle, Clock, XCircle, Eye, FileText, Shield, AlertTriangle, Upload, Send, Trash2 } from 'lucide-react';

interface Document {
  id: string;
  name: string;
  type: string;
  status: 'pending' | 'verified' | 'rejected' | 'approved';
  uploaded_at: string;
  reviewed_at?: string;
  admin_notes?: string;
  file_url?: string;
}

interface DocRequest {
  id: string;
  title: string;
  description?: string;
  status: string;
  created_at: string;
  response_file_url?: string;
}

interface AgreementRecord {
  id: string;
  title: string;
  type: 'broker_agreement' | 'rate_confirmation';
  file_url: string;
  carrier_id?: string;
  status: string;
  created_at: string;
  signed_at?: string;
  signed_by_name?: string;
  decline_reason?: string;
  signed_copy_url?: string;
  load_number?: string;
  broker_viewed_at?: string;
  delivery_status?: string;
  pod_file_url?: string;
  delivered_at?: string;
}

interface Carrier {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  created_at: string;
  documents: Document[];
  application_status?: string;
  application_data?: any;
  submitted_at?: string;
  docRequests?: DocRequest[];
  agreements?: AgreementRecord[];
  rateConfirmations?: AgreementRecord[];
}

type CarrierTab = 'status' | 'documents' | 'requests' | 'insurance' | 'agreements' | 'ratecons';

const CompanyCarriersPage = () => {
  const { profile } = useCompanyProfile();
  const [Carriers, setCarriers] = useState<Carrier[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedCarrierId, setExpandedCarrierId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<CarrierTab>('status');

  const [requestingFromCarrier, setRequestingFromCarrier] = useState<string | null>(null);
  const [requestDocType, setRequestDocType] = useState('');
  const [requestNotes, setRequestNotes] = useState('');

  const [uploadingFor, setUploadingFor] = useState<{ carrierId: string; type: 'broker_agreement' | 'rate_confirmation' } | null>(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadLoadNumber, setUploadLoadNumber] = useState('');
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchivedRC, setShowArchivedRC] = useState(false);
 

  
    useEffect(() => {
    if (!profile?.company_code) return;
    if (Carriers.length > 0) return;
    fetchCarriers();
  }, [profile?.company_code]);

  const fetchCarriers = async () => {
    try {
      const { data: applications } = await supabase
        .from('applications')
        .select('user_id, status, submitted_at, form_data')
        .eq('company_code', profile?.company_code)
        .order('submitted_at', { ascending: false });

      if (!applications || applications.length === 0) {
        setCarriers([]);
        setLoading(false);
        return;
      }

      const userIds = [...new Set(applications.map(app => app.user_id))];

      const CarriersWithDocs = await Promise.all(
        userIds.map(async (userId) => {
          const { data: carrierProfile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single();

          const { data: docs } = await supabase
            .from('documents')
            .select('*')
            .eq('user_id', userId)
            .order('uploaded_at', { ascending: false });

          const { data: requests } = await supabase
            .from('document_requests')
            .select('*')
            .eq('carrier_id', userId)
            .eq('company_code', profile?.company_code)
            .order('created_at', { ascending: false });

          const { data: agreements } = await supabase
            .from('agreements')
            .select('*')
            .eq('company_code', profile?.company_code)
            .eq('type', 'broker_agreement')
            .or(`carrier_id.eq.${userId},carrier_id.is.null`)
            .order('created_at', { ascending: false });

          const { data: rateCons } = await supabase
            .from('agreements')
            .select('*')
            .eq('company_code', profile?.company_code)
            .eq('type', 'rate_confirmation')
            .eq('carrier_id', userId)
            .order('created_at', { ascending: false });

          const app = applications.find(a => a.user_id === userId);

          return {
            ...carrierProfile,
            documents: docs || [],
            docRequests: requests || [],
            agreements: agreements || [],
            rateConfirmations: rateCons || [],
            application_status: app?.status,
            application_data: app?.form_data,
            submitted_at: app?.submitted_at,
          };
        })
      );

      setCarriers(CarriersWithDocs.filter(c => c?.id));
    } catch (error) {
      console.error('Error fetching Carriers:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (CarrierId: string) => {
    if (expandedCarrierId === CarrierId) {
      setExpandedCarrierId(null);
    } else {
      setExpandedCarrierId(CarrierId);
      setActiveTab('status');
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const handleCarrierStatus = async (
  carrierId: string,
  status: 'approved' | 'rejected'
) => {
  try {
    const { error } = await supabase
      .from('applications')
      .update({ status })
      .eq('user_id', carrierId)
      .eq('company_code', profile?.company_code);

    if (error) throw error;

    setCarriers(prev =>
      prev.map(c =>
        c.id === carrierId ? { ...c, application_status: status } : c
      )
    );

    alert(
      `Carrier ${
        status === 'approved' ? 'approved' : 'rejected'
      } successfully!`
    );
  } catch (error) {
    console.error('Error updating carrier status:', error);
    alert('Failed to update carrier status');
  }
};

  const handleArchiveAgreement = async (id: string, carrierId: string, type: string) => {
    try {
      const { error } = await supabase
        .from('agreements')
        .update({ status: 'archived' })
        .eq('id', id);

      if (error) throw error;

      setCarriers(prev =>
        prev.map(c => {
          if (c.id === carrierId) {
            if (type === 'broker_agreement') {
              return {
                ...c,
                agreements: (c.agreements || []).map(a =>
                  a.id === id ? { ...a, status: 'archived' } : a
                ),
              };
            } else {
              return {
                ...c,
                rateConfirmations: (c.rateConfirmations || []).map(a =>
                  a.id === id ? { ...a, status: 'archived' } : a
                ),
              };
            }
          }
          return c;
        })
      );
    } catch (error) {
      console.error('Error archiving:', error);
      alert('Failed to archive.');
    }
  };
  const handleDeleteAgreement = async (id: string, carrierId: string, type: string) => {
    if (!confirm('Are you sure you want to delete this?')) return;

    try {
      const { error } = await supabase.from('agreements').delete().eq('id', id);
      if (error) throw error;

      setCarriers(prev =>
        prev.map(c => {
          if (c.id === carrierId) {
            if (type === 'broker_agreement') {
              return { ...c, agreements: (c.agreements || []).filter(a => a.id !== id) };
            } else {
              return { ...c, rateConfirmations: (c.rateConfirmations || []).filter(a => a.id !== id) };
            }
          }
          return c;
        })
      );
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Failed to delete.');
    }
  };
const handleRequestDocument = async () => {
    if (!requestDocType.trim() || !requestingFromCarrier) return;

    try {
      const { error } = await supabase
        .from('document_requests')
        .insert({
          carrier_id: requestingFromCarrier,
          requested_by: profile?.id,
          company_code: profile?.company_code,
          title: requestDocType,
          description: requestNotes,
          status: 'pending',
        });

      if (error) throw error;

      setCarriers(prev =>
        prev.map(c => {
          if (c.id === requestingFromCarrier) {
            return {
              ...c,
              docRequests: [
                { id: Date.now().toString(), title: requestDocType, description: requestNotes, status: 'pending', created_at: new Date().toISOString() },
                ...(c.docRequests || []),
              ],
            };
          }
          return c;
        })
      );

      alert('Document request sent!');
      setRequestingFromCarrier(null);
      setRequestDocType('');
      setRequestNotes('');
    } catch (error) {
      console.error('Error requesting document:', error);
      alert('Failed to send request');
    }
  };
  const handleUploadAgreement = async () => {
   if (!uploadFile || !uploadTitle.trim() || !uploadingFor) return;

    // Block rate con if carrier's insurance is expired
    if (uploadingFor.type === 'rate_confirmation') {
      const carrier = Carriers.find(c => c.id === uploadingFor.carrierId);
      const expDate = carrier?.application_data?.insurance?.expirationDate;
      if (expDate) {
        const expirationDate = new Date(expDate);
        const today = new Date();
        if (expirationDate < today) {
          alert('Cannot send rate confirmation to a carrier with expired insurance. Please request an updated COI first.');
          return;
        }
      }
    }

    setUploading(true);
    try {
      const fileName = `${profile?.company_code}/${uploadingFor.type}/${Date.now()}_${uploadFile.name}`;

      const { error: uploadError } = await supabase.storage
        .from('carrier-documents')
        .upload(fileName, uploadFile);

      if (uploadError) throw uploadError;

      const { data: inserted, error: insertError } = await supabase
        .from('agreements')
        .insert({
          company_code: profile?.company_code,
          uploaded_by: profile?.id,
          title: uploadTitle,
          type: uploadingFor.type,
          file_url: fileName,
          carrier_id: uploadingFor.carrierId,
          load_number: uploadingFor.type === 'rate_confirmation' ? uploadLoadNumber : null,
          status: 'active',
        })
        .select()
        .single();

      if (insertError) throw insertError;

      setCarriers(prev =>
        prev.map(c => {
          if (c.id === uploadingFor.carrierId) {
            const newRecord: AgreementRecord = {
              id: inserted.id,
              title: uploadTitle,
              type: uploadingFor.type,
              file_url: fileName,
              carrier_id: uploadingFor.carrierId,
              status: 'active',
              created_at: new Date().toISOString(),
            };

            if (uploadingFor.type === 'broker_agreement') {
              return { ...c, agreements: [newRecord, ...(c.agreements || [])] };
            } else {
              return { ...c, rateConfirmations: [newRecord, ...(c.rateConfirmations || [])] };
            }
          }
          return c;
        })
      );

      alert(
        `${uploadingFor.type === 'broker_agreement'
          ? 'Agreement'
          : 'Rate Confirmation'} sent successfully!`
      );

      setUploadingFor(null);
      setUploadTitle('');
      setUploadFile(null);
      setUploadLoadNumber('');
    } catch (error) {
      console.error('Error uploading:', error);
      alert('Failed to upload. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleViewDocument = async (fileUrl: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('carrier-documents')
        .createSignedUrl(fileUrl, 3600);

      if (error || !data?.signedUrl) {
        alert('Unable to load document');
        return;
      }

      window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error('Error viewing document:', err);
      alert('Unable to load document');
    }
  };

  const getDocumentIcon = (status: string) => {
    switch (status) {
      case 'verified':
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status?: string) => {
    if (status === 'approved') return 'border-green-500';
    if (status === 'rejected') return 'border-red-500';
    return 'border-yellow-400';
  };

  const getStatusBg = (status?: string) => {
    if (status === 'approved') return 'bg-green-500';
    if (status === 'rejected') return 'bg-red-500';
    return 'bg-yellow-400';
  };

  const tabClass = (tab: CarrierTab) =>
    `px-4 py-2 text-sm font-medium border-b-2 cursor-pointer whitespace-nowrap ${
      activeTab === tab
        ? 'border-blue-600 text-blue-600'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`;
  const getInsuranceStatus = (carrier: Carrier) => {
    const expDate = carrier.application_data?.insurance?.expirationDate;

    if (!expDate)
      return { status: 'missing', label: 'No insurance on file', color: 'text-gray-500' };

    const exp = new Date(expDate);
    const today = new Date();
    const daysUntil = Math.ceil(
      (exp.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysUntil < 0)
      return { status: 'expired', label: `Expired ${formatDate(expDate)}`, color: 'text-red-600' };

    if (daysUntil <= 30)
      return {
        status: 'expiring',
        label: `Expires ${formatDate(expDate)} (${daysUntil} days)`,
        color: 'text-yellow-600',
      };

    return {
      status: 'valid',
      label: `Valid until ${formatDate(expDate)}`,
      color: 'text-green-600',
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading Carriers...</div>
      </div>
    );
  }
const filteredCarriers = Carriers.filter(c => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const name = `${c.first_name} ${c.last_name}`.toLowerCase();
    const email = (c.email || '').toLowerCase();
    const mc = (c.application_data?.companyInfo?.mcNumber || '').toLowerCase();
    const dot = (c.application_data?.companyInfo?.dotNumber || '').toLowerCase();
    const loadNums = (c.rateConfirmations || []).map(r => (r.load_number || '').toLowerCase());
    const docTitles = [...(c.agreements || []), ...(c.rateConfirmations || [])].map(a => (a.title || '').toLowerCase());
    return name.includes(q) || email.includes(q) || mc.includes(q) || dot.includes(q) || loadNums.some(l => l.includes(q)) || docTitles.some(t => t.includes(q));
  });

  // Auto-expand and switch tab when searching

  return (
    <div className="p-6">
     {/* Search Bar */}
      <div className="mb-4">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by carrier name, email, MC#, DOT#, or load number..."
          className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div className="bg-white rounded-lg shadow">
        {filteredCarriers.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No carriers yet. Share your company code to get started.
          </div>
        )}

       {filteredCarriers.map((Carrier) => {
          const isExpanded = expandedCarrierId === Carrier.id;
          const pendingDocs = Carrier.documents.filter(d => d.status === 'pending').length;
          const pendingReqs = (Carrier.docRequests || []).filter(r => r.status === 'pending').length;
          const insurance = getInsuranceStatus(Carrier);
          const pendingAgreements = (Carrier.agreements || []).filter(a => a.status === 'active').length;
          const pendingRateCons = (Carrier.rateConfirmations || []).filter(a => a.status === 'active').length;

          return (
            <div
              key={Carrier.id}
              className={`border-2 border-l-4 ${getStatusColor(
                Carrier.application_status
              )} mb-2 rounded-lg`}
            >
              {/* Collapsed Header */}
              <div
                onClick={() => toggleExpand(Carrier.id)}
                className="p-4 cursor-pointer hover:bg-gray-50"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                    <div>
                      <h3 className="text-lg font-semibold">
                        {Carrier.first_name} {Carrier.last_name}
                      </h3>
                     <p className="text-base text-gray-600">{Carrier.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <span
                      className={`text-xs font-semibold px-2 py-1 rounded-full ${
                        Carrier.application_status === 'approved'
                          ? 'bg-green-100 text-green-700'
                          : Carrier.application_status === 'rejected'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}
                    >
                      {Carrier.application_status === 'approved'
                        ? 'Approved'
                        : Carrier.application_status === 'rejected'
                        ? 'Rejected'
                        : 'Pending'}
                    </span>

                    {pendingDocs > 0 && (
                      <span className="text-xs bg-yellow-500 text-white rounded-full px-2 py-0.5">
                        {pendingDocs} docs
                      </span>
                    )}

                    {insurance.status === 'expired' && (
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                    )}

                    {insurance.status === 'expiring' && (
                      <AlertTriangle className="h-4 w-4 text-yellow-500" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="border-t">
                  {/* TAB BAR */}
                  <div
                    className={`flex bg-gray-50 px-4 border-b-2 ${getStatusColor(
                      Carrier.application_status
                    )} overflow-x-auto`}
                  >
                    <button
                      onClick={() => setActiveTab('status')}
                      className={tabClass('status')}
                    >
                      <span className="flex items-center gap-1.5">
                        <Shield className="h-4 w-4" /> Status
                        <span
                          className={`h-2 w-2 rounded-full ${getStatusBg(
                            Carrier.application_status
                          )} inline-block`}
                        ></span>
                      </span>
                    </button>

                    <button
                      onClick={() => setActiveTab('documents')}
                      className={tabClass('documents')}
                    >
                      <span className="flex items-center gap-1.5">
                        <FileText className="h-4 w-4" /> Documents
                        {pendingDocs > 0 && (
                          <span className="bg-yellow-500 text-white text-xs rounded-full px-1.5 py-0.5 ml-1">
                            {pendingDocs}
                          </span>
                        )}
                      </span>
                    </button>

                    <button
                      onClick={() => setActiveTab('requests')}
                      className={tabClass('requests')}
                    >
                      <span className="flex items-center gap-1.5">
                        <Clock className="h-4 w-4" /> Requests
                        {pendingReqs > 0 && (
                          <span className="bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 ml-1">
                            {pendingReqs}
                          </span>
                        )}
                      </span>
                    </button>

                    <button
                      onClick={() => setActiveTab('insurance')}
                      className={tabClass('insurance')}
                    >
                      <span className="flex items-center gap-1.5">
                        {insurance.status === 'expired' ? (
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                        ) : insurance.status === 'expiring' ? (
                          <AlertTriangle className="h-4 w-4 text-yellow-500" />
                        ) : (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        )}
                        Insurance
                      </span>
                    </button>

                    <button
                      onClick={() => setActiveTab('agreements')}
                      className={tabClass('agreements')}
                    >
                      <span className="flex items-center gap-1.5">
                        <FileText className="h-4 w-4" /> Agreements
                        {pendingAgreements > 0 && (
                          <span className="bg-blue-500 text-white text-xs rounded-full px-1.5 py-0.5 ml-1">
                            {pendingAgreements}
                          </span>
                        )}
                      </span>
                    </button>

                    <button
                      onClick={() => setActiveTab('ratecons')}
                      className={tabClass('ratecons')}
                    >
                      <span className="flex items-center gap-1.5">
                        <Send className="h-4 w-4" /> Rate Cons
                        {pendingRateCons > 0 && (
                          <span className="bg-purple-500 text-white text-xs rounded-full px-1.5 py-0.5 ml-1">
                            {pendingRateCons}
                          </span>
                        )}
                      </span>
                    </button>
                  </div>
                  {/* TAB CONTENT */}
                  <div className="p-4">

                    {/* STATUS TAB */}
                    {activeTab === 'status' && (
                      <div>
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-gray-500">Carrier Status</p>
                              <p className={`text-lg font-semibold ${
                                Carrier.application_status === 'approved'
                                  ? 'text-green-600'
                                  : Carrier.application_status === 'rejected'
                                  ? 'text-red-600'
                                  : 'text-yellow-600'
                              }`}>
                                {Carrier.application_status === 'approved'
                                  ? '✓ Approved'
                                  : Carrier.application_status === 'rejected'
                                  ? '✗ Rejected'
                                  : '● Pending Review'}
                              </p>
                            </div>

                            <div className="flex gap-2">
                              {Carrier.application_status !== 'approved' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCarrierStatus(Carrier.id, 'approved');
                                  }}
                                  className="px-4 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                                >
                                  ✓ Approve Carrier
                                </button>
                              )}

                              {Carrier.application_status !== 'rejected' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCarrierStatus(Carrier.id, 'rejected');
                                  }}
                                  className="px-4 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                                >
                                  ✗ Reject Carrier
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 grid grid-cols-2 gap-4">
                          <div className="p-3 bg-gray-50 rounded">
                            <p className="text-xs text-gray-500">Phone</p>
                            <p className="text-sm font-medium">
                              {Carrier.phone || 'Not provided'}
                            </p>
                          </div>

                          <div className="p-3 bg-gray-50 rounded">
                            <p className="text-xs text-gray-500">Applied</p>
                            <p className="text-sm font-medium">
                              {Carrier.submitted_at
                                ? formatDate(Carrier.submitted_at)
                                : 'N/A'}
                            </p>
                          </div>

                          <div className="p-3 bg-gray-50 rounded">
                            <p className="text-xs text-gray-500">MC Number</p>
                            <p className="text-sm font-medium">
                              {Carrier.application_data?.companyInfo?.mcNumber || 'N/A'}
                            </p>
                          </div>

                          <div className="p-3 bg-gray-50 rounded">
                            <p className="text-xs text-gray-500">DOT Number</p>
                            <p className="text-sm font-medium">
                              {Carrier.application_data?.companyInfo?.dotNumber || 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* DOCUMENTS TAB */}
                    {activeTab === 'documents' && (
                      <div>
                        {Carrier.documents.length === 0 ? (
                          <p className="text-gray-500 text-sm">
                            No documents uploaded yet.
                          </p>
                        ) : (
                          Carrier.documents.map((doc) => (
                            <div
                              key={doc.id}
                              className="flex items-center justify-between p-3 border rounded mb-2"
                            >
                              <div className="flex items-center gap-3">
                                {getDocumentIcon(doc.status)}
                                <div>
                                  <p className="text-sm font-medium">{doc.name}</p>
                                  <p className="text-xs text-gray-500">
                                    {doc.type} • Uploaded {formatDate(doc.uploaded_at)}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                <span
                                  className={`text-xs font-semibold capitalize px-2 py-1 rounded ${
                                    doc.status === 'verified' || doc.status === 'approved'
                                      ? 'bg-green-100 text-green-700'
                                      : doc.status === 'rejected'
                                      ? 'bg-red-100 text-red-700'
                                      : 'bg-yellow-100 text-yellow-700'
                                  }`}
                                >
                                  {doc.status}
                                </span>

                                {doc.file_url && (
                                  <button
                                    onClick={() =>
                                      handleViewDocument(doc.file_url!)
                                    }
                                    className="flex items-center px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                                  >
                                    <Eye className="h-3 w-3 mr-1" /> View
                                  </button>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    {/* REQUESTS TAB */}
                    {activeTab === 'requests' && (
                      <div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setRequestingFromCarrier(Carrier.id);
                          }}
                          className="mb-4 px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                        >
                          + Request Document
                        </button>

                        {(!Carrier.docRequests ||
                          Carrier.docRequests.length === 0) ? (
                          <p className="text-gray-500 text-sm">
                            No document requests sent.
                          </p>
                        ) : (
                          Carrier.docRequests.map((req) => (
                            <div
                              key={req.id}
                              className="flex items-center justify-between p-3 border rounded mb-2"
                            >
                              <div className="flex items-center gap-3">
                                {req.status === 'fulfilled' ? (
                                  <CheckCircle className="h-4 w-4 text-green-500" />
                                ) : (
                                  <Clock className="h-4 w-4 text-yellow-500" />
                                )}

                                <div>
                                  <p className="text-sm font-medium">
                                    {req.title}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Requested {formatDate(req.created_at)}
                                    {req.description &&
                                      ` • ${req.description}`}
                                  </p>
                                </div>
                              </div>

                              <span
                                className={`text-xs font-semibold capitalize px-2 py-1 rounded ${
                                  req.status === 'fulfilled'
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}
                              >
                                {req.status}
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    {/* INSURANCE TAB */}
                    {activeTab === 'insurance' && (
                      <div>
                        <div
                          className={`p-4 rounded-lg border ${
                            insurance.status === 'expired'
                              ? 'bg-red-50 border-red-200'
                              : insurance.status === 'expiring'
                              ? 'bg-yellow-50 border-yellow-200'
                              : insurance.status === 'valid'
                              ? 'bg-green-50 border-green-200'
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {insurance.status === 'expired' ? (
                              <AlertTriangle className="h-6 w-6 text-red-500" />
                            ) : insurance.status === 'expiring' ? (
                              <AlertTriangle className="h-6 w-6 text-yellow-500" />
                            ) : insurance.status === 'valid' ? (
                              <CheckCircle className="h-6 w-6 text-green-500" />
                            ) : (
                              <XCircle className="h-6 w-6 text-gray-400" />
                            )}

                            <div>
                              <p className="font-semibold text-gray-900">
                                Insurance Status
                              </p>
                              <p className={`text-sm ${insurance.color}`}>
                                {insurance.label}
                              </p>
                            </div>
                          </div>
                        </div>
                        {insurance.status === 'expired' && (
                          <div className="mt-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const newDate = prompt('Enter new expiration date (YYYY-MM-DD):');
                                if (!newDate) return;
                                const parsed = new Date(newDate);
                                if (isNaN(parsed.getTime())) { alert('Invalid date format'); return; }
                                const updatedData = JSON.parse(JSON.stringify(Carrier.application_data));
                                updatedData.insurance = updatedData.insurance || {};
                                updatedData.insurance.expirationDate = newDate;
                                supabase.from('applications').update({ form_data: updatedData }).eq('user_id', Carrier.id).eq('company_code', profile?.company_code).then(({ error: err }) => {
                                  if (err) { alert('Failed to update'); return; }
                                  setCarriers(prev => prev.map(c => c.id === Carrier.id ? { ...c, application_data: updatedData } : c));
                                  alert('Insurance expiration updated!');
                                });
                              }}
                              className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                            >
                              Update Expiration Date
                            </button>
                          </div>
                        )}

                        <div className="mt-4 grid grid-cols-2 gap-4">
                          <div className="p-3 bg-gray-50 rounded">
                            <p className="text-xs text-gray-500">
                              Cargo Coverage
                            </p>
                            <p className="text-sm font-medium">
                              {Carrier.application_data?.insurance
                                ?.cargoCoverage
                                ? `$${Number(
                                    Carrier.application_data.insurance
                                      .cargoCoverage
                                  ).toLocaleString()}`
                                : 'N/A'}
                            </p>
                          </div>

                          <div className="p-3 bg-gray-50 rounded">
                            <p className="text-xs text-gray-500">
                              Liability Coverage
                            </p>
                            <p className="text-sm font-medium">
                              {Carrier.application_data?.insurance
                                ?.liabilityCoverage
                                ? `$${Number(
                                    Carrier.application_data.insurance
                                      .liabilityCoverage
                                  ).toLocaleString()}`
                                : 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* AGREEMENTS TAB */}
                    {activeTab === 'agreements' && (
                      <div>
                        <button
                          onClick={() =>
                            setUploadingFor({
                              carrierId: Carrier.id,
                              type: 'broker_agreement',
                            })
                          }
                          className="mb-4 px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 flex items-center gap-2"
                        >
                          <Upload className="h-4 w-4" /> Send Agreement
                        </button>

                        {(!Carrier.agreements ||
                          Carrier.agreements.length === 0) ? (
                          <p className="text-gray-500 text-sm">
                            No agreements sent to this carrier.
                          </p>
                        ) : (
                         Carrier.agreements.map((agreement) => (
                            <div
                              key={agreement.id}
                              className={`flex items-center justify-between p-3 border rounded mb-2 ${!agreement.broker_viewed_at ? 'bg-blue-50 border-blue-200' : ''}`}
                              onClick={async () => {
                                if (!agreement.broker_viewed_at) {
                                  await supabase.from('agreements').update({ broker_viewed_at: new Date().toISOString() }).eq('id', agreement.id);
                                  setCarriers(prev => prev.map(c => c.id === Carrier.id ? { ...c, agreements: (c.agreements || []).map(a => a.id === agreement.id ? { ...a, broker_viewed_at: new Date().toISOString() } : a) } : c));
                                }
                              }}
                            >
                              <div className="flex items-center gap-3">
                                <FileText className="h-4 w-4 text-blue-500" />
                                <div>
                                  <p className="text-sm font-medium">
                                    {agreement.title}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Sent {formatDate(agreement.created_at)}
                                    {agreement.signed_by_name &&
                                      ` • Signed by: ${agreement.signed_by_name}`}
                                    {agreement.decline_reason &&
                                      ` • Reason: ${agreement.decline_reason}`}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                               <span
                                  className={`text-xs font-semibold px-2 py-1 rounded ${
                                    agreement.delivery_status === 'pod_submitted'
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : agreement.delivery_status === 'delivered'
                                      ? 'bg-orange-100 text-orange-700'
                                      : agreement.status === 'signed'
                                      ? 'bg-green-100 text-green-700'
                                      : agreement.status === 'declined'
                                      ? 'bg-red-100 text-red-700'
                                      : agreement.status === 'archived'
                                      ? 'bg-gray-100 text-gray-700'
                                      : 'bg-blue-100 text-blue-700'
                                  }`}
                                >
                                  {agreement.delivery_status === 'pod_submitted'
                                    ? '📄 Load Delivered — POD Submitted'
                                    : agreement.delivery_status === 'delivered'
                                    ? '🚚 Delivered'
                                    : agreement.status === 'signed'
                                    ? '✓ Signed & Accepted'
                                    : agreement.status === 'declined'
                                    ? '✗ Declined'
                                    : agreement.status === 'archived'
                                    ? '📁 Archived'
                                    : '⏳ Awaiting Signature'}
                                </span>

                                {agreement.signed_copy_url && (
                                  <button
                                    onClick={() => handleViewDocument(agreement.signed_copy_url!)}
                                    className="flex items-center px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100"
                                  >
                                   <Eye className="h-3 w-3 mr-1" /> Signed Rate Con
                                  </button>
                                )}

                                <button
                                  onClick={() =>
                                    handleViewDocument(agreement.file_url)
                                  }
                                  className="flex items-center px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                                >
                                  <Eye className="h-3 w-3 mr-1" /> View
                                </button>

                                {(agreement.status === 'declined' ||
                                  agreement.status === 'archived') && (
                                  <button
                                    onClick={() =>
                                      setUploadingFor({
                                        carrierId: Carrier.id,
                                        type: 'broker_agreement',
                                      })
                                    }
                                    className="flex items-center px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                                  >
                                    <Send className="h-3 w-3 mr-1" /> Resend
                                  </button>
                                )}

                                {agreement.status !== 'archived' && (
                                  <button
                                    onClick={() =>
                                      handleArchiveAgreement(
                                        agreement.id,
                                        Carrier.id,
                                        'broker_agreement'
                                      )
                                    }
                                    className="flex items-center px-2 py-1 text-xs bg-gray-50 text-gray-700 rounded hover:bg-gray-100"
                                  >
                                    Archive
                                  </button>
                                )}

                                <button
                                  onClick={() =>
                                    handleDeleteAgreement(
                                      agreement.id,
                                      Carrier.id,
                                      'broker_agreement'
                                    )
                                  }
                                  className="flex items-center px-2 py-1 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100"
                                >
                                  <Trash2 className="h-3 w-3 mr-1" /> Delete
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    {/* RATE CONS TAB */}
                    {activeTab === 'ratecons' && (
                      <div>
                        <button
                          onClick={() =>
                            setUploadingFor({
                              carrierId: Carrier.id,
                              type: 'rate_confirmation',
                            })
                          }
                          className="mb-4 px-4 py-2 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 flex items-center gap-2"
                        >
                          <Upload className="h-4 w-4" /> Send Rate Confirmation
                        </button>
<div className="flex gap-2 mb-3">
                          <button
                            onClick={() => setShowArchivedRC(false)}
                            className={`px-3 py-1 text-xs rounded-md font-medium ${!showArchivedRC ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                          >
                            Active
                          </button>
                          <button
                            onClick={() => setShowArchivedRC(true)}
                            className={`px-3 py-1 text-xs rounded-md font-medium ${showArchivedRC ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                          >
                            📁 Archived
                          </button>
                        </div>
                        {(!Carrier.rateConfirmations ||
                          Carrier.rateConfirmations.filter(r => showArchivedRC ? r.status === 'archived' : r.status !== 'archived').length === 0) ? (
                          <p className="text-gray-500 text-sm">
                            {showArchivedRC ? 'No archived rate confirmations.' : 'No rate confirmations sent to this carrier.'}
                          </p>
                        ) : (
                         Carrier.rateConfirmations.filter(r => showArchivedRC ? r.status === 'archived' : r.status !== 'archived').map((rc) => (
                            <div
                              key={rc.id}
                              className={`flex items-center justify-between p-3 border rounded mb-2 ${
                                searchQuery && ((rc.load_number || '').toLowerCase().includes(searchQuery.toLowerCase()) || (rc.title || '').toLowerCase().includes(searchQuery.toLowerCase()))
                                  ? 'bg-yellow-50 border-yellow-300 ring-2 ring-yellow-200'
                                  : !rc.broker_viewed_at ? 'bg-blue-50 border-blue-200' : ''
                              }`}
                              onClick={async () => {
                                if (!rc.broker_viewed_at) {
                                  await supabase.from('agreements').update({ broker_viewed_at: new Date().toISOString() }).eq('id', rc.id);
                                  setCarriers(prev => prev.map(c => c.id === Carrier.id ? { ...c, rateConfirmations: (c.rateConfirmations || []).map(r => r.id === rc.id ? { ...r, broker_viewed_at: new Date().toISOString() } : r) } : c));
                                }
                              }}
                            >
                              <div className="flex items-center gap-3">
                                <Send className="h-4 w-4 text-purple-500" />
                                <div>
                               <p className="text-sm font-medium">
                                    {rc.title}
                                    {rc.load_number && (
                                      <span className="ml-2 text-xs font-normal text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
                                        Load #{rc.load_number}
                                      </span>
                                    )}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Sent {formatDate(rc.created_at)}
                                    {rc.signed_by_name &&
                                      ` • Signed by: ${rc.signed_by_name}`}
                                    {rc.decline_reason &&
                                      ` • Reason: ${rc.decline_reason}`}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                               <span
                                  className={`text-xs font-semibold px-2 py-1 rounded ${
                                    rc.delivery_status === 'pod_submitted'
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : rc.delivery_status === 'delivered'
                                      ? 'bg-orange-100 text-orange-700'
                                      : rc.status === 'signed'
                                      ? 'bg-green-100 text-green-700'
                                      : rc.status === 'declined'
                                      ? 'bg-red-100 text-red-700'
                                      : rc.status === 'archived'
                                      ? 'bg-gray-100 text-gray-700'
                                      : 'bg-purple-100 text-purple-700'
                                  }`}
                                >
                                  {rc.delivery_status === 'pod_submitted'
                                    ? '📄 Delivered — POD Attached'
                                    : rc.delivery_status === 'delivered'
                                    ? '🚚 Delivered'
                                    : rc.status === 'signed'
                                    ? '✓ Signed & Accepted'
                                    : rc.status === 'declined'
                                    ? '✗ Declined'
                                    : rc.status === 'archived'
                                    ? '📁 Archived'
                                    : '⏳ Awaiting Signature'}
                                </span>
{rc.signed_copy_url && (
                                  <button
                                    onClick={() => handleViewDocument(rc.signed_copy_url!)}
                                    className="flex items-center px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100"
                                  ><Eye className="h-3 w-3 mr-1" /> Signed Rate Con
                                  </button>
                                )}
                              
                                {rc.pod_file_url && (
                                  <button
                                    onClick={() => handleViewDocument(rc.pod_file_url!)}
                                    className="flex items-center px-2 py-1 text-xs bg-orange-50 text-orange-700 rounded hover:bg-orange-100"
                                  >
                                    <Eye className="h-3 w-3 mr-1" /> View POD
                                  </button>
                                )}
                                <button
                                  onClick={() =>
                                    handleViewDocument(rc.file_url)
                                  }
                                  className="flex items-center px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                                >
                                  <Eye className="h-3 w-3 mr-1" /> View
                                </button>

                                {(rc.status === 'declined' ||
                                  rc.status === 'archived') && (
                                  <button
                                    onClick={() =>
                                      setUploadingFor({
                                        carrierId: Carrier.id,
                                        type: 'rate_confirmation',
                                      })
                                    }
                                    className="flex items-center px-2 py-1 text-xs bg-purple-50 text-purple-700 rounded hover:bg-purple-100"
                                  >
                                    <Send className="h-3 w-3 mr-1" /> Resend
                                  </button>
                                )}

                                {rc.status !== 'archived' && (
                                  <button
                                    onClick={() =>
                                      handleArchiveAgreement(
                                        rc.id,
                                        Carrier.id,
                                        'rate_confirmation'
                                      )
                                    }
                                    className="flex items-center px-2 py-1 text-xs bg-gray-50 text-gray-700 rounded hover:bg-gray-100"
                                  >
                                    Archive
                                  </button>
                                )}

                                <button
                                  onClick={() =>
                                    handleDeleteAgreement(
                                      rc.id,
                                      Carrier.id,
                                      'rate_confirmation'
                                    )
                                  }
                                  className="flex items-center px-2 py-1 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100"
                                >
                                  <Trash2 className="h-3 w-3 mr-1" /> Delete
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}

                  </div>
                </div>
              )}
            </div>
          );
        })}
     </div>

      {/* Request Document Modal */}
      {requestingFromCarrier && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Request Document</h3>
         
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Document Type *</label>
              <select value={requestDocType} onChange={(e) => setRequestDocType(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                <option value="">Select document type...</option>
                <optgroup label="Insurance & Compliance">
                  <option value="COI">Certificate of Insurance (COI)</option>
                  <option value="Updated COI">Updated COI (Renewal)</option>
                </optgroup>
                <optgroup label="Tax & Payment">
                  <option value="W9">W-9 Form</option>
                  <option value="Voided Check">Voided Check</option>
                </optgroup>
                <optgroup label="Authority Documents">
                  <option value="MC Authority">MC Authority Letter</option>
                  <option value="DOT Authority">DOT Authority Letter</option>
                </optgroup>
                <optgroup label="Agreements">
                  <option value="Signed Rate Confirmation">Signed Rate Confirmation</option>
                  <option value="Signed Broker-Carrier Agreement">Signed Broker-Carrier Agreement</option>
                </optgroup>
                <optgroup label="Certifications">
                  <option value="TWIC Card">TWIC Card</option>
                  <option value="Hazmat Certification">Hazmat Certification</option>
                  <option value="Driver License">Driver License (CDL)</option>
                </optgroup>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
              <textarea value={requestNotes} onChange={(e) => setRequestNotes(e.target.value)} placeholder="Add any specific instructions..." className="w-full px-3 py-2 border border-gray-300 rounded-md" rows={3} />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setRequestingFromCarrier(null); setRequestDocType(''); setRequestNotes(''); }} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
              <button onClick={handleRequestDocument} disabled={!requestDocType} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">Send Request</button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Agreement / Rate Confirmation Modal */}
      {uploadingFor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">
              {uploadingFor.type === 'broker_agreement' ? 'Send Agreement' : 'Send Rate Confirmation'}
            </h3>
            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
              <p className="text-sm text-blue-800">
                Sending to: <strong>{Carriers.find(c => c.id === uploadingFor.carrierId)?.first_name} {Carriers.find(c => c.id === uploadingFor.carrierId)?.last_name}</strong>
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input type="text" value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)} placeholder={uploadingFor.type === 'broker_agreement' ? 'e.g. Broker-Carrier Agreement 2026' : 'e.g. Rate Con - Chicago to Dallas - $2,500'} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
            {uploadingFor && uploadingFor.type === 'rate_confirmation' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Load Number *</label>
                <input type="text" value={uploadLoadNumber} onChange={(e) => setUploadLoadNumber(e.target.value)} placeholder="e.g. LD-2026-001" className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
            )}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">PDF File *</label>
              <input type="file" accept=".pdf" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
            <div className="flex justify-end gap-2">
               <button onClick={() => { setUploadingFor(null); setUploadTitle(''); setUploadFile(null); setUploadLoadNumber(''); }} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
              <button onClick={handleUploadAgreement} disabled={!uploadFile || !uploadTitle.trim() || uploading} className={`px-4 py-2 text-white rounded-md disabled:opacity-50 ${uploadingFor.type === 'broker_agreement' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-purple-600 hover:bg-purple-700'}`}>
                {uploading ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyCarriersPage;
